import { useAuth } from '@agentnet/auth';
import { Alert } from '@agentnet/components';
import { Grid, IconButton, Typography } from '@material-ui/core';
import { CancelOutlined } from '@material-ui/icons';
import { getFileInfoProps, savePhoneNumber } from 'api/file/file-api';
import {
  ContactValidationResult,
  IdentityVerificationResult,
  ISavePhoneNumberPayload,
  Party,
} from 'api/file/interfaces/get-file-order-insights';
import clsx from 'clsx';
import {
  MAX_ATTEMPTS_VERIFICATION_TEXT,
  MAX_IDENTITY_CHECK_ATTEMPTS,
  OFFER_DETAILS_PENDING,
  offerMessage,
  RETRY_VERIFICATION_TEXT,
  VERIFICATION_TIME_LIMIT_MESSAGE,
} from 'features/help/constants';
import { FileDataContext, FileDataContextInterface } from 'hooks/FileDataContext';
import useAsync from 'hooks/useAsync';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import HourglassTopOutlined from 'ui-kit/icons/HourglassTopOutlined';
import { AgentNetDropdownSelector } from 'ui-kit/inputs';
import { theme } from 'ui-kit/theme/theme';
import { getVerificationExpiryTime } from 'utilities/dateFormatter';
import ContactInfoDisplay from './ContactInfoDisplay';
import useStyles from './ListStyles';
import PhoneNumberModal from './PhoneNumberModal';
import VerificationResults from './VerificationResults';

interface IVerifyPhoneNumber {
  qaPrefix: string;
  isVerifcationNotDone: boolean;
  isRiskChecked: boolean;
  isRiskFound: boolean;
  isResponsePendingFromParty: boolean;
  index: number;
  type: 'ContactValidation' | 'EnhancedIdentityVerification';
  party: Party;
  description: string;
  firmId: string;
  hasReadOnlyFraudValidation?: boolean;
  hasFraudValidation?: boolean;
  validationChecks: {
    validateContactInfoStatus: 'idle' | 'pending' | 'success' | 'error';
    validateContactInfoStatusResults: ContactValidationResult | null;
    doValidateContactInfo: (...args: any[]) => Promise<void>;
    enhancedIdentityVerification: (...args: any[]) => Promise<void>;
    enhancedIdentityVerificationStatusResults: IdentityVerificationResult | null;
    enhancedIdentityVerificationStatus: 'idle' | 'pending' | 'success' | 'error';
  };
}

const documentTypeOptions = [
  {
    name: "North American Driver's License/Identification Cards",
    value: "North American Driver's License/Identification Cards",
  },
  {
    name: 'Passport Booklet - Coming Soon',
    value: 'Passport Booklet - Coming Soon',
    disabled: true,
  },
  {
    name: 'Other Documents - Coming Soon',
    value: 'Other Documents - Coming Soon',
    disabled: true,
  },
];

const languageOptions = [
  {
    name: 'English',
    value: 'English',
  },
  {
    name: 'Spanish - Coming Soon',
    value: 'Spanish - Coming Soon',
    disabled: true,
  },
  {
    name: 'French - Coming Soon',
    value: 'French - Coming Soon',
    disabled: true,
  },
];

const VerifyPhoneNumber = ({
  qaPrefix,
  isVerifcationNotDone,
  isRiskChecked,
  isRiskFound,
  description,
  isResponsePendingFromParty,
  type = 'ContactValidation',
  party,
  firmId,
  validationChecks,
  hasReadOnlyFraudValidation,
  hasFraudValidation,
  index,
}: IVerifyPhoneNumber) => {
  const {
    doValidateContactInfo,
    enhancedIdentityVerification,
    validateContactInfoStatus,
    validateContactInfoStatusResults,
    enhancedIdentityVerificationStatusResults,
    enhancedIdentityVerificationStatus,
  } = validationChecks;
  const classes = useStyles();
  const [isPartyDialogOpened, setOpenPartyDialog] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const { fileId = '' } = useParams<getFileInfoProps>();
  const fileDataCtx: FileDataContextInterface = useContext(FileDataContext) ?? {};
  const { loadFileOrderInsightsData } = fileDataCtx;
  const isContactValidation = type === 'ContactValidation';

  const hideEditParty = {
    ContactValidation: !!party.phoneNumber || !!party.emailAddress,
    EnhancedIdentityVerification: !!party.phoneNumber,
  };

  const partyInfo = {
    firmId,
    fileId: fileId,
    partyId: party.id,
    partyType: party.partyType,
    party: party.filePartyType,
    isSpouse: party.isSpouse,
    phone: party.phoneNumber,
    email: party.emailAddress,
    address: party.address,
    firstName: party.firstName,
    lastName: party.lastName,
  };

  const { addGlobalMsg } = useGlobalMessages();
  const { getAccessToken } = useAuth();
  const [identityVerificationOptions, setOptionValue] = useState({
    documentType: "North American Driver's License/Identification Cards",
    language: 'English',
  });

  const handleDocumentTypeChange = (val: string) => {
    setOptionValue((prev) => ({ ...prev, documentType: val }));
  };

  const handleLanguageChange = (val: string) => {
    setOptionValue((prev) => ({ ...prev, language: val }));
  };

  const services = async (payload: Party): Promise<ISavePhoneNumberPayload> => {
    const token = await getAccessToken();
    const currentPayload = {
      firmId,
      fileId: fileId,
      partyId: payload.id,
      partyType: payload.partyType,
      party: payload.filePartyType,
      isSpouse: payload.isSpouse,
      phone: payload.phoneNumber,
      email: payload.emailAddress,
      address: payload.address,
    };
    return await savePhoneNumber(currentPayload, token);
  };

  const {
    execute: saveModalDetails,
    status: saveModalDetailsStatus,
    errors: saveModalDetailsErrors,
  } = useAsync<ISavePhoneNumberPayload>(services, false);

  useEffect(() => {
    if (saveModalDetailsStatus === 'error') {
      const errorMessages = [...(saveModalDetailsErrors ?? [])];
      if (errorMessages?.length) {
        errorMessages.map((err) => {
          addGlobalMsg({
            message: err,
            type: 'error',
          });
        });
      }
    }
  }, [saveModalDetailsStatus]);

  const openPartyDialog = () => {
    setOpenPartyDialog(true);
  };
  const closePartyDialog = () => {
    setOpenPartyDialog(false);
  };

  const modifiedParty = useMemo(() => {
    const tempParty = { ...party };
    if (party.address.isForeignAddress) {
      tempParty.address = {
        isForeignAddress: false,
        address1: '',
        address2: '',
        city: '',
        postalCode: '',
        stateOrProvince: '',
      };
    }
    return tempParty;
  }, [party.address.isForeignAddress]);

  const [persistedIdentityResults, setPersistedIdentityResults] = useState<IdentityVerificationResult | null>(
    party.enhancedIdentifyVerification || null,
  );

  useEffect(() => {
    if (enhancedIdentityVerificationStatusResults) {
      setPersistedIdentityResults(enhancedIdentityVerificationStatusResults);
    }
  }, [enhancedIdentityVerificationStatusResults]);

  const identityResults = persistedIdentityResults;

  if (!hasFraudValidation && !hasReadOnlyFraudValidation) {
    return (
      <Typography variant="body1" className={clsx(classes.marginBottom, classes.typographyFontSize)}>
        {`${description} ${offerMessage['nonAdmin']}`}
      </Typography>
    );
  }

  if (isRiskChecked) {
    return <div>Checking Risk, isRiskFound: {String(isRiskFound)}</div>;
  }

  if (isResponsePendingFromParty) {
    return <div>Pending</div>;
  }

  if (hideEditParty[type]) {
    if (isContactValidation) {
      const contactInfo = party.contactValidation || validateContactInfoStatusResults;
      return contactInfo ? (
        <ContactInfoDisplay {...contactInfo} />
      ) : (
        <AgentNetButton
          onClick={() => doValidateContactInfo(partyInfo)}
          variant="contained"
          color="primary"
          data-testid={`${qaPrefix}-button-validate-contact-info-${index}`}
          size="small"
          disabled={!hasFraudValidation || validateContactInfoStatus === 'pending'}
          loading={validateContactInfoStatus === 'pending'}
        >
          Validate Contact Info
        </AgentNetButton>
      );
    }
    const lastAttempt = identityResults?.attempt === 2 ? 'time' : 'times';
    return identityResults?.transactionStatus === 'Completed' ? (
      <VerificationResults
        {...identityResults}
        enhancedIdentityVerification={() => enhancedIdentityVerification(partyInfo)}
        fileId={fileId}
        index={index}
        enhancedIdentityVerificationStatus={enhancedIdentityVerificationStatus}
      />
    ) : identityResults?.transactionStatus === 'Pending' ? (
      <Alert severity="warning" icon={<HourglassTopOutlined fill={theme.palette.status.pending} />}>
        <strong>Pending Response from Party</strong> <br /> Request will time out at{' '}
        {getVerificationExpiryTime(identityResults?.submitted ?? '')}
      </Alert>
    ) : (
      <>
        <Typography variant="body1" className={clsx(classes.marginBottomLarge, classes.typographyFontSize)}>
          {VERIFICATION_TIME_LIMIT_MESSAGE}
        </Typography>
        <Grid container spacing={3} className={classes.marginBottom}>
          <Grid item sm={6}>
            <AgentNetDropdownSelector
              name="documentType"
              label="Document Type"
              required
              options={documentTypeOptions}
              data-testid={`${qaPrefix}-select-menu-document-type-${index}`}
              value={identityVerificationOptions.documentType}
              id="documentType"
              menuOption={handleDocumentTypeChange}
              dropdowntype="outlined"
            />
          </Grid>
          <Grid item sm={6}>
            <AgentNetDropdownSelector
              name="language"
              label="Language"
              required
              options={languageOptions}
              value={identityVerificationOptions?.language}
              id="language"
              menuOption={handleLanguageChange}
              dropdowntype="outlined"
              data-testid={`${qaPrefix}-select-menu-language-${index}`}
            />
          </Grid>
        </Grid>
        <AgentNetButton
          onClick={() => enhancedIdentityVerification(partyInfo)}
          variant="contained"
          color="primary"
          data-testid={`${qaPrefix}-button-send-verification-request-${index}`}
          size="small"
          disabled={
            identityResults?.attempt === MAX_IDENTITY_CHECK_ATTEMPTS ||
            !hasFraudValidation ||
            enhancedIdentityVerificationStatus === 'pending'
          }
          loading={enhancedIdentityVerificationStatus === 'pending'}
        >
          Send Verification Request
        </AgentNetButton>
        {identityResults?.transactionStatus === 'Failed' && identityResults?.attempt ? (
          <Typography
            variant="body1"
            className={clsx(classes.marginTop, classes.typographyFontSize, classes.retryText)}
          >
            {identityResults.attempt === MAX_IDENTITY_CHECK_ATTEMPTS
              ? MAX_ATTEMPTS_VERIFICATION_TEXT
              : RETRY_VERIFICATION_TEXT.replace(
                  'count',
                  (MAX_IDENTITY_CHECK_ATTEMPTS - identityResults.attempt).toString(),
                ).replace('times', lastAttempt)}
          </Typography>
        ) : null}
        {isVerifcationNotDone && <div>Verification Not Done</div>}
      </>
    );
  }

  return (
    <>
      <Alert
        className={classes.infoAlert}
        severity={'info'}
        icon={<CancelOutlined color="secondary" />}
        action={
          <IconButton
            aria-label="Edit Party"
            data-testid={`${qaPrefix}-button-edit-party-${index}`}
            size="medium"
            onClick={openPartyDialog}
          >
            <Typography className={classes.bold}>Edit Party</Typography>
          </IconButton>
        }
      >
        {OFFER_DETAILS_PENDING[type]}
      </Alert>
      <PhoneNumberModal
        open={isPartyDialogOpened}
        qaPrefix={qaPrefix}
        index={index}
        disablePrimary={!hasFraudValidation}
        isActionInProgress={showLoader}
        onClose={closePartyDialog}
        onConfirm={(newValue: Party) => {
          const { phoneNumber, emailAddress, ...rest } = newValue;
          const updatedPartyInfo = {
            ...partyInfo,
            ...rest,
            phone: phoneNumber,
            email: emailAddress,
          };
          setShowLoader(true);
          saveModalDetails(newValue)
            .then(() =>
              type === 'ContactValidation'
                ? doValidateContactInfo(updatedPartyInfo)
                : enhancedIdentityVerification(updatedPartyInfo),
            )
            .then(() => {
              setShowLoader(false);
              loadFileOrderInsightsData?.(fileId, true);
              closePartyDialog();
            })
            .catch(() => {
              setShowLoader(false);
            });
        }}
        type={type}
        party={modifiedParty}
      />
    </>
  );
};

export default VerifyPhoneNumber;
